import { makeAutoObservable } from 'mobx'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { apiClient } from 'api/trpc/trpc-client'
import { OAuthClientId } from '@gap/backend/app-services/oauth/models/OAuthEnums'
import { OAuthClientDto } from '@gap/backend/app-services/oauth/models/OAuthClientDto'
import * as Sentry from '@sentry/browser'
import { messageError } from 'reusable/mui/modals/message-error'
import { messageInfo } from 'reusable/mui/modals/message-info'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { GridColDef } from '@mui/x-data-grid'

type ClientInfo = OAuthClientDto.clientInfo
type ClientConfig = OAuthClientDto.clientConfig

interface OAuthClientStoreProps { }

export default class OAuthClientStore {
   sp
   clients: ClientInfo[] = []
   clientConfig: ClientConfig | null = null
   isLoading = false
   isLoadingConfig = false
   isRegenerating = false
   copiedField: string | null = null
   configModal = new ModalState()

   constructor(sp: OAuthClientStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
      this.loadClients()
   }

   /**
    * Grid columns configuration for OAuth client list
    */
   get gridColumns(): GridColDef[] {
      return [
         {
            field: 'clientId',
            headerName: 'Client ID',
            flex: 1,
            minWidth: 120
         },
         {
            field: 'name',
            headerName: 'Client Name',
            flex: 2,
            minWidth: 200
         },
         {
            field: 'description',
            headerName: 'Description',
            flex: 3,
            minWidth: 300
         }
      ]
   }

   /**
    * Get unique row ID for data grid
    */
   getRowId = (row: ClientInfo) => row.clientId

   /**
    * Handle grid cell click for viewing config
    */
   onCellClickHandler = (params: any) => {
      this.handleViewConfigById(params.row.clientId as OAuthClientId)
   }

   /**
 * Handle view config by client ID
 */
   handleViewConfigById = async (oAuthClientId: OAuthClientId) => {
      // Open modal immediately for better UX
      this.configModal.open()

      await this.loadClientConfig(oAuthClientId)

      // If config is null (no secret exists), generate one and try again
      if (!this.clientConfig) {
         await this.generateSecret(oAuthClientId)
         await this.loadClientConfig(oAuthClientId)
      }
   }

   /**
    * Loads list of OAuth clients
    */
   loadClients = async () => {
      try {
         this.isLoading = true
         const oAuthClients = await apiClient.oAuthClient.listClients.query()
         this.clients = oAuthClients
      } catch (error) {
         Sentry.captureException(error, { tags: { component: 'OAuthClientStore', action: 'loadClients' } })
         await messageError('Load Error', 'Failed to load OAuth clients. Please try again.')
      } finally {
         this.isLoading = false
      }
   }

   /**
    * Loads configuration details for a specific client
    */
   loadClientConfig = async (oAuthClientId: OAuthClientId) => {
      try {
         this.isLoadingConfig = true
         const backendConfig = await apiClient.oAuthClient.getClientConfig.query({
            clientId: oAuthClientId
         })
         this.clientConfig = backendConfig
      } catch (error) {
         Sentry.captureException(error, { tags: { component: 'OAuthClientStore', action: 'loadClientConfig', clientId: oAuthClientId } })
         await messageError('Configuration Error', `Failed to load configuration for ${oAuthClientId}. Please try again.`)
         this.clientConfig = null
      } finally {
         this.isLoadingConfig = false
      }
   }

   /**
    * Clears loaded client configuration
    */
   clearClientConfig = () => {
      this.clientConfig = null
   }

   /**
    * Regenerates client secret
    */
   regenerateSecret = async (oAuthClientId: OAuthClientId) => {
      try {
         this.isRegenerating = true
         await apiClient.oAuthClient.regenerateSecret.mutate({ clientId: oAuthClientId })
         await messageInfo('Secret regenerated successfully', `New client secret generated for ${oAuthClientId}.`)
      } catch (error) {
         Sentry.captureException(error, { tags: { component: 'OAuthClientStore', action: 'regenerateSecret', clientId: oAuthClientId } })
         await messageError('Regeneration Error', `Failed to regenerate secret for ${oAuthClientId}. Please try again.`)
      } finally {
         this.isRegenerating = false
      }
   }

   /**
    * Generates initial client secret
    */
   generateSecret = async (oAuthClientId: OAuthClientId) => {
      try {
         this.isRegenerating = true
         await apiClient.oAuthClient.generateSecret.mutate({ clientId: oAuthClientId })
         await messageInfo('Secret generated successfully', `Initial client secret created for ${oAuthClientId}.`)
      } catch (error) {
         Sentry.captureException(error, { tags: { component: 'OAuthClientStore', action: 'generateSecret', clientId: oAuthClientId } })
         await messageError('Generation Error', `Failed to generate secret for ${oAuthClientId}. Please try again.`)
      } finally {
         this.isRegenerating = false
      }
   }



   /**
    * Sets the copied field for clipboard feedback
    */
   setCopiedField = (fieldName: string | null) => {
      this.copiedField = fieldName
      if (fieldName) {
         // Auto-clear after 2 seconds
         setTimeout(() => {
            this.copiedField = null
         }, 2000)
      }
   }

   /**
    * Handle copy to clipboard from event
    */
   handleCopyToClipboard = (event: React.MouseEvent<HTMLButtonElement>) => {
      const value = event.currentTarget.dataset.value
      const fieldName = event.currentTarget.dataset.fieldName
      if (value && fieldName) {
         this.copyToClipboard(value, fieldName)
      }
   }

   /**
    * Copies text to clipboard with feedback
    */
   copyToClipboard = async (text: string, fieldName: string) => {
      try {
         await navigator.clipboard.writeText(text)
         this.setCopiedField(fieldName)
      } catch (error) {
         Sentry.captureException(error, { tags: { component: 'OAuthClientStore', action: 'copyToClipboard' } })
         await messageError('Clipboard Error', 'Failed to copy to clipboard. Please copy manually.')
      }
   }

   /**
    * Closes the config dialog
    */
   closeDialog = () => {
      this.configModal.close()
      this.clearClientConfig()
   }

   /**
    * Handle regenerate secret button click
    */
   handleRegenerateSecret = async () => {
      if (!this.clientConfig) return

      await this.regenerateSecret(this.clientConfig.clientId as OAuthClientId)
      await this.loadClientConfig(this.clientConfig.clientId as OAuthClientId)
   }
}

export const { Provider: OAuthClientStoreProvider, useProvidedStore: useOAuthClientStore } =
   createStoreContext(OAuthClientStore)
